import {
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  startAuthentication,
  startRegistration,
  WebAuthnAbortService,
} from '@simplewebauthn/browser';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/index.js';
import type { components } from '@/lib/api/schema.gen.js';
import { queryKeys } from '@/lib/query-client.js';

export type Passkey = components['schemas']['Passkey'];

interface CeremonyStart {
  ceremonyId: string;
  options: Record<string, unknown>;
}

/** Does this browser support passkeys at all? */
export const supportsPasskeys = browserSupportsWebAuthn;

/**
 * Does the browser support conditional UI — offering passkeys from the email
 * field's autofill, with no button pressed?
 *
 * It is the nicest version of this flow: the user focuses the field and their
 * passkey is simply offered. Firefox and older Safari do not have it, hence
 * the explicit button as well.
 */
export const supportsPasskeyAutofill = browserSupportsWebAuthnAutofill;

/**
 * Thrown when the user dismisses the platform prompt.
 *
 * Cancelling is not an error — it is a decision. Showing "authentication
 * failed" to someone who chose to close the dialog is both wrong and alarming,
 * so this is distinguishable and handled silently.
 */
/**
 * Aborts any ceremony currently in flight.
 *
 * The browser permits only one WebAuthn ceremony at a time, and a conditional
 * one started on mount can outlive the page. Without this, navigating away
 * mid-ceremony leaves it pending — which blocks the next one and resolves into
 * a component that no longer exists.
 */
export function cancelPasskeyCeremony(): void {
  WebAuthnAbortService.cancelCeremony();
}

export class PasskeyCancelledError extends Error {
  constructor() {
    super('Passkey prompt was dismissed');
    this.name = 'PasskeyCancelledError';
  }
}

/**
 * Thrown when the browser refused without ever showing a prompt.
 *
 * Distinct from a cancellation, and the distinction matters: a cancellation is
 * a decision and deserves silence, whereas this leaves somebody pressing a
 * button that appears to do nothing.
 */
export class PasskeyUnavailableError extends Error {
  constructor() {
    super(
      'Your browser or device could not create a passkey for this site. ' +
        'You can sign in with a password instead.',
    );
    this.name = 'PasskeyUnavailableError';
  }
}

/**
 * The shortest a genuine cancellation can take.
 *
 * WebAuthn reports a cancellation and an outright refusal with the *same*
 * `NotAllowedError`, deliberately: the spec will not say why a ceremony failed,
 * because the reason can leak whether a credential exists. The only thing that
 * separates them is time. Dismissing a prompt means seeing it, moving to it and
 * closing it; a browser with no usable authenticator rejects in milliseconds.
 */
const CANCEL_FLOOR_MS = 600;

/**
 * Turns a ceremony failure into something the caller can act on.
 *
 * Treating every `NotAllowedError` as a cancellation — which this did — makes
 * "no authenticator available", "the platform refused" and "the request timed
 * out" all silent, and the button simply does nothing.
 */
function classifyCeremonyFailure(error: unknown, elapsedMs: number): Error {
  if (!(error instanceof Error)) return new Error(String(error));
  if (error.name === 'AbortError') return new PasskeyCancelledError();
  if (error.name !== 'NotAllowedError') return error;
  return elapsedMs < CANCEL_FLOOR_MS ? new PasskeyUnavailableError() : new PasskeyCancelledError();
}

/** Runs a WebAuthn ceremony, classifying a failure by how long it took. */
export async function runCeremony<T>(ceremony: () => Promise<T>): Promise<T> {
  const startedAt = Date.now();
  try {
    return await ceremony();
  } catch (error) {
    throw classifyCeremonyFailure(error, Date.now() - startedAt);
  }
}

/**
 * Can this device actually create a passkey, rather than merely understand the
 * API? `supportsPasskeys` only answers the second question, which is why a
 * button could be offered on a device that then refused silently.
 */
export async function canCreatePasskey(): Promise<boolean> {
  if (!browserSupportsWebAuthn()) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    // Older browsers lack the probe. Offer the button and let the ceremony
    // speak for itself rather than hiding a feature that might work.
    return true;
  }
}

export interface PasskeyLoginResult {
  status: 'AUTHENTICATED';
  session: components['schemas']['Session'];
}

/**
 * Signs in with a passkey.
 *
 * Two round trips, as every WebAuthn ceremony is: fetch a challenge, have the
 * authenticator sign it, send the signature back. No email is typed at any
 * point — the browser offers whichever passkeys it holds for this domain.
 */
export function usePasskeyLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (options: { rememberDevice?: boolean; useAutofill?: boolean } = {}) => {
      const start = await api.post<CeremonyStart>('/auth/passkeys/authenticate/options');

      const response = await runCeremony(() =>
        startAuthentication({
          optionsJSON: start.options as never,
          useBrowserAutofill: options.useAutofill ?? false,
        }),
      );

      return api.post<PasskeyLoginResult>('/auth/passkeys/authenticate/verify', {
        ceremonyId: start.ceremonyId,
        response,
        rememberDevice: options.rememberDevice ?? false,
      });
    },
    onSuccess: (result) => {
      queryClient.setQueryData(queryKeys.session, result.session);
    },
  });
}

export function usePasskeys() {
  return useQuery({
    queryKey: queryKeys.passkeys(),
    queryFn: () => api.get<{ items: Passkey[] }>('/auth/passkeys'),
  });
}

/** Enrols a new passkey on the signed-in account. */
export function useEnrolPasskey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (label?: string) => {
      // Asked before the round trip, so a device that cannot create a passkey
      // says so at once instead of after a 60-second ceremony that shows no
      // prompt. `supportsPasskeys` only answers whether the API exists.
      if (!(await canCreatePasskey())) throw new PasskeyUnavailableError();

      const start = await api.post<CeremonyStart>('/auth/passkeys/register/options');

      const response = await runCeremony(() =>
        startRegistration({ optionsJSON: start.options as never }),
      );

      return api.post<Passkey>('/auth/passkeys/register/verify', {
        ceremonyId: start.ceremonyId,
        response,
        ...(label ? { label } : {}),
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.passkeys() }),
  });
}

export function useRenamePasskey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; label: string }) =>
      api.patch<Passkey>(`/auth/passkeys/${encodeURIComponent(input.id)}`, {
        label: input.label,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.passkeys() }),
  });
}

export function useRevokePasskey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/auth/passkeys/${encodeURIComponent(id)}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.passkeys() }),
  });
}
